import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AuctionDataService from "../services/AuctionService";

import { debugLog, errorLog } from "../utils/debugUtils";
import { getRejectedError, toRejectedPayload } from "../utils/sliceErrorUtils";
import { prepareAuctionPackageObject } from "../utils/auctionUtils";

const initialState = {
	topAutoBids: [],

	list: [],
	selectedPkg: null,
	selectedPkgItem: null,
	activePackageDetailRequestId: null,

	status: {
		retrieve: "idle",
		retrieveOne: "idle",
		create: "idle",
		update: "idle",
		retrievePackageItem: "idle",
		getTopAutoBidsPerUser: "idle",
		updatePackageItem: "idle",
		updatePackageBidder: "idle",
	},
	error: {
		retrieve: null,
		retrieveOne: null,
		create: null,
		update: null,
		retrievePackageItem: null,
		getTopAutoBidsPerUser: null,
		updatePackageItem: null,
		updatePackageBidder: null,
	},
};

// Package CRUD paths now preserve backend messages so Auction dialogs/forms stay
// aligned with the Baseline/Scenario error-surface contract.
export const retrievePackages = createAsyncThunk(
	"auctions/retrievePackage",
	async (_, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.getAllPackages();
			debugLog(3, "[auctionSlice][FLOW] retrievePackages response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] retrievePackages rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package retrieve failed."));
		}
	}
);

export const retrieveOnePackage = createAsyncThunk(
	"auctions/retrieveOnePackage",
	async (id, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.getOnePackage(id);
			debugLog(3, "[auctionSlice][FLOW] retrieveOnePackage response", { response: res.data });
			return prepareAuctionPackageObject(res.data);
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] retrieveOnePackage rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package retrieve failed."));
		}
	}
);

export const retrieveOnePackageByUuid = createAsyncThunk(
	"auctions/retrieveOnePackageByUuid",
	async (uuid, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.findPackageByUuid(uuid);
			debugLog(3, "[auctionSlice][FLOW] retrieveOnePackageByUuid response", { response: res.data });
			return prepareAuctionPackageObject(res.data);
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] retrieveOnePackageByUuid rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package retrieve failed."));
		}
	}
);

export const createPackage = createAsyncThunk(
	"auctions/createPackage",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.createPackage(data);
			debugLog(3, "[auctionSlice][FLOW] createPackage response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] createPackage rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package create failed."));
		}
	}
);

export const updatePackage = createAsyncThunk(
	"auctions/updatePackage",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.updatePackage(id, data);
			debugLog(3, "[auctionSlice][FLOW] updatePackage response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] updatePackage rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package update failed."));
		}
	}
);

export const retrieveOnePackageItem = createAsyncThunk(
	"auctions/retrieveOnePackageItem",
	async (id, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.getOnePackageItem(id);
			debugLog(3, "[auctionSlice][FLOW] retrieveOnePackageItem response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] retrieveOnePackageItem rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package item retrieve failed."));
		}
	}
);

export const updatePackageItem = createAsyncThunk(
	"auctions/updatePackageItem",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.updatePackageItem(id, data);
			debugLog(3, "[auctionSlice][FLOW] updatePackageItem response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] updatePackageItem rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package item update failed."));
		}
	}
);

export const updatePackageBidder = createAsyncThunk(
	"auctions/updatePackageBidder",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.updatePackageBidder(id, data);
			debugLog(3, "[auctionSlice][FLOW] updatePackageBidder response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] updatePackageBidder rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package bidder update failed."));
		}
	}
);

export const getTopAutoBidsPerUser = createAsyncThunk(
	"auctions/fetchTopAutoBids",
	async (packageId = null, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.getTopAutoBidsPerUser(packageId);
			debugLog(3, "[auctionSlice][FLOW] getTopAutoBidsPerUser response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] getTopAutoBidsPerUser rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Top auto-bids retrieve failed."));
		}
	}
);

export const updatePackageStatus = createAsyncThunk(
	"auctions/updatePackageStatus",
	async ({ id, status_id, updatedBy }, { rejectWithValue }) => {
		try {
			const res = await AuctionDataService.updatePackageStatus(id, status_id, updatedBy);
			debugLog(3, "[auctionSlice][FLOW] updatePackageStatus response", { response: res.data });
			return { id, status_id }; // Minimal return; adjust as needed
		} catch (err) {
			errorLog(4, "[auctionSlice][PROCESS] updatePackageStatus rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Package status update failed."));
		}
	}
);

const auctionSlice = createSlice({
	name: "auctions",
	initialState,
	reducers: {
		setSelectedPkg: (state, action) => {
			// safeguard against null/undefined/falsey payload
			state.selectedPkg = action.payload ?? null;
			state.activePackageDetailRequestId = null;
			if (state.status) state.status.retrieveOne = "idle";
			if (state.error) state.error.retrieveOne = null;
			debugLog(4, "[auctionSlice][PROCESS] setSelectedPkg", { payload: action.payload });
		},
		clearSelectedPkg: (state) => {
			state.selectedPkg = null;
			state.activePackageDetailRequestId = null;
			if (state.status) state.status.retrieveOne = "idle";
			if (state.error) state.error.retrieveOne = null;
			debugLog(4, "[auctionSlice][PROCESS] clearSelectedPkg", {});
		},
		setSelectedPkgItem: (state, action) => {
			state.selectedPkgItem = action.payload ?? null;
			debugLog(4, "[auctionSlice][PROCESS] setSelectedPkgItem", { payload: action.payload });
		},
		clearSelectedPkgItem: (state) => {
			state.selectedPkgItem = null;
			debugLog(4, "[auctionSlice][PROCESS] clearSelectedPkgItem", {});
		},
		resetError: (state, action) => {
			// Optional: reset all errors, or just one type based on payload
			if (action.payload) {
				// Reset only one error key, e.g. { type: "create" }
				state.error[action.payload.type] = null;
				debugLog(4, "[auctionSlice][PROCESS] resetError one", { type: action.payload.type });
			} else {
				// Reset all error keys
				Object.keys(state.error).forEach((key) => {
					state.error[key] = null;
				});
				debugLog(4, "[auctionSlice][PROCESS] resetError all", {});
			}
		},
	},
	extraReducers: (builder) => {
		builder
			// retrievePackages
			.addCase(retrievePackages.pending, (state) => {
				state.status.retrieve = "loading";
				state.error.retrieve = null;
			})
			.addCase(retrievePackages.fulfilled, (state, action) => {
				state.status.retrieve = "succeeded";
				state.list = action.payload;
			})
			.addCase(retrievePackages.rejected, (state, action) => {
				state.status.retrieve = "failed";
				state.error.retrieve = getRejectedError(action, "Package retrieve failed.");
				debugLog(4, "[auctionSlice][PROCESS] retrievePackages rejected", { error: action.error });
			})

			// The active request id makes route changes and out-of-order responses inert.
			// Explicit selection changes clear it in setSelectedPkg/clearSelectedPkg.
			// retrieveOnePackage
			.addCase(retrieveOnePackage.pending, (state, action) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
				state.activePackageDetailRequestId = action.meta.requestId;
			})
			.addCase(retrieveOnePackage.fulfilled, (state, action) => {
				if (state.activePackageDetailRequestId !== action.meta.requestId) return;
				state.status.retrieveOne = "succeeded";
				state.selectedPkg = action.payload;
				state.activePackageDetailRequestId = null;
			})
			.addCase(retrieveOnePackage.rejected, (state, action) => {
				if (state.activePackageDetailRequestId !== action.meta.requestId) return;
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "Package retrieve failed.");
				state.activePackageDetailRequestId = null;
				debugLog(4, "[auctionSlice][PROCESS] retrieveOnePackage rejected", { error: action.error });
			})

			// retrieveOnePackageByUuid
			.addCase(retrieveOnePackageByUuid.pending, (state, action) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
				state.activePackageDetailRequestId = action.meta.requestId;
			})
			.addCase(retrieveOnePackageByUuid.fulfilled, (state, action) => {
				if (state.activePackageDetailRequestId !== action.meta.requestId) return;
				state.status.retrieveOne = "succeeded";
				state.selectedPkg = action.payload;
				state.activePackageDetailRequestId = null;
			})
			.addCase(retrieveOnePackageByUuid.rejected, (state, action) => {
				if (state.activePackageDetailRequestId !== action.meta.requestId) return;
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "Package retrieve failed.");
				state.activePackageDetailRequestId = null;
				debugLog(4, "[auctionSlice][PROCESS] retrieveOnePackageByUuid rejected", { error: action.error });
			})

			// createPackage
			.addCase(createPackage.pending, (state) => {
				state.status.create = "loading";
				state.error.create = null;
			})
			.addCase(createPackage.fulfilled, (state, action) => {
				state.status.create = "succeeded";
				state.list.push(action.payload);
			})
			.addCase(createPackage.rejected, (state, action) => {
				state.status.create = "failed";
				state.error.create = getRejectedError(action, "Package create failed.");
				debugLog(4, "[auctionSlice][PROCESS] createPackage rejected", { error: action.error });
			})

			// updatePackage
			.addCase(updatePackage.pending, (state) => {
				state.status.update = "loading";
				state.error.update = null;
			})
			.addCase(updatePackage.fulfilled, (state, action) => {
				state.status.update = "succeeded";
				// state.selectedItem = action.payload;
				const index = state.list.findIndex((pkg) => pkg.id === action.payload.id);
				if (index !== -1) state.list[index] = action.payload;

				debugLog(4, "[auctionSlice][PROCESS] updatePackage fulfilled updated package", {
					payload: action.payload,
				});
			})
			.addCase(updatePackage.rejected, (state, action) => {
				state.status.update = "failed";
				state.error.update = getRejectedError(action, "Package update failed.");
				debugLog(4, "[auctionSlice][PROCESS] updatePackage rejected", { error: action.error });
			})

			// retrieveOnePackageItem
			.addCase(retrieveOnePackageItem.pending, (state) => {
				state.status.retrievePackageItem = "loading";
				state.error.retrievePackageItem = null;
			})
			.addCase(retrieveOnePackageItem.fulfilled, (state, action) => {
				state.status.retrievePackageItem = "succeeded";
				state.selectedPkgItem = action.payload;
				debugLog(4, "[auctionSlice][PROCESS] retrieveOnePackageItem fulfilled", {
					payload: action.payload,
				});
			})
			.addCase(retrieveOnePackageItem.rejected, (state, action) => {
				state.status.retrievePackageItem = "failed";
				state.error.retrievePackageItem = getRejectedError(action, "Package item retrieve failed.");
				debugLog(4, "[auctionSlice][PROCESS] retrieveOnePackageItem rejected", { error: action.error });
			})

			// updatePackageItem
			.addCase(updatePackageItem.pending, (state) => {
				state.status.updatePackageItem = "loading";
				state.error.updatePackageItem = null;
			})
			.addCase(updatePackageItem.fulfilled, (state, action) => {
				state.status.updatePackageItem = "succeeded";
				state.updatePkgItem = action.payload;
				debugLog(4, "[auctionSlice][PROCESS] updatePackageItem fulfilled", {
					payload: action.payload,
				});
			})
			.addCase(updatePackageItem.rejected, (state, action) => {
				state.status.updatePackageItem = "failed";
				state.error.updatePackageItem = getRejectedError(action, "Package item update failed.");
				debugLog(4, "[auctionSlice][PROCESS] updatePackageItem rejected", { error: action.error });
			})

			// updatePackageBidder
			.addCase(updatePackageBidder.pending, (state) => {
				state.status.updatePackageBidder = "loading";
				state.error.updatePackageBidder = null;
			})
			.addCase(updatePackageBidder.fulfilled, (state) => {
				state.status.updatePackageBidder = "succeeded";
			})
			.addCase(updatePackageBidder.rejected, (state, action) => {
				state.status.updatePackageBidder = "failed";
				state.error.updatePackageBidder = getRejectedError(action, "Package bidder update failed.");
				debugLog(4, "[auctionSlice][PROCESS] updatePackageBidder rejected", { error: action.error });
			})

			// fetchTopAutoBids
			.addCase(getTopAutoBidsPerUser.pending, (state) => {
				state.status.getTopAutoBidsPerUser = "loading";
				state.error.getTopAutoBidsPerUser = null;
			})
			.addCase(getTopAutoBidsPerUser.fulfilled, (state, action) => {
				state.status.getTopAutoBidsPerUser = "succeeded";
				state.topAutoBids = action.payload;
			})
			.addCase(getTopAutoBidsPerUser.rejected, (state, action) => {
				state.status.getTopAutoBidsPerUser = "failed";
				state.error.getTopAutoBidsPerUser = getRejectedError(action, "Top auto-bids retrieve failed.");
				debugLog(4, "[auctionSlice][PROCESS] getTopAutoBidsPerUser rejected", {
					error: action.error,
				});
			})

			// updatePackageStatus
			.addCase(updatePackageStatus.pending, (state) => {
				state.status.update = "loading";
				state.error.update = null;
			})
			.addCase(updatePackageStatus.fulfilled, (state, action) => {
				state.status.update = "succeeded";
				// The status thunk returns a patch, so retain the loaded package fields
				// while applying archive/restore state, as Baseline and Scenario do.
				const idx = state.list.findIndex((b) => b.id === action.payload.id);
				if (idx !== -1) {
					state.list[idx] = {
						...state.list[idx],
						...action.payload,
					};
				}
				if (state.selectedPkg && state.selectedPkg.id === action.payload.id) {
					state.selectedPkg = {
						...state.selectedPkg,
						...action.payload,
					};
				}

				debugLog(4, "[auctionSlice][PROCESS] updatePackageStatus fulfilled", {
					payload: action.payload,
				});
			})
			.addCase(updatePackageStatus.rejected, (state, action) => {
				state.status.update = "failed";
				state.error.update = getRejectedError(action, "Package status update failed.");
				debugLog(4, "[auctionSlice][PROCESS] updatePackageStatus rejected", {
					error: action.error,
				});
			});
	},
});

const { reducer } = auctionSlice;
export default reducer;

// Export action creators for UI
export const { setSelectedPkg, clearSelectedPkg, resetError, setSelectedPkgItem, clearSelectedPkgItem } =
	auctionSlice.actions;
